<template>
  <div class="contact-form-widget" :style="widgetStyles">
    <div v-if="widget.title" class="form-title mb-4">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>
    
    <div v-if="widget.description" class="form-description mb-4">
      <p :style="descriptionStyles">{{ widget.description }}</p>
    </div>

    <form @submit.prevent="submitForm" class="contact-form">
      <div class="row g-3">
        <!-- First Name Field -->
        <div class="col-md-6" v-if="widget.showName">
          <label class="form-label" :class="{ 'required': widget.nameRequired }">
            Όνομα
          </label>
          <input 
            type="text" 
            v-model="formData.firstName"
            class="form-control"
            placeholder="Όνομα"
            :required="widget.nameRequired"
          >
        </div>

        <!-- Last Name Field -->
        <div class="col-md-6" v-if="widget.showName">
          <label class="form-label">
            Επώνυμο
          </label>
          <input 
            type="text" 
            v-model="formData.lastName"
            class="form-control"
            placeholder="Επώνυμο"
          >
        </div>

        <!-- Email Field -->
        <div class="col-md-6">
          <label class="form-label required">
            {{ widget.emailLabel || 'Email' }}
          </label>
          <input 
            type="email" 
            v-model="formData.email"
            class="form-control"
            :placeholder="widget.emailPlaceholder || 'email@example.com'"
            required
          >
        </div>

        <!-- Phone Field -->
        <div class="col-md-6" v-if="widget.showPhone">
          <label class="form-label" :class="{ 'required': widget.phoneRequired }">
            {{ widget.phoneLabel || 'Τηλέφωνο' }}
          </label>
          <input 
            type="tel" 
            v-model="formData.phone"
            class="form-control"
            :placeholder="widget.phonePlaceholder || '+30 210 123 4567'"
            :required="widget.phoneRequired"
          >
        </div>

        <!-- Subject Field -->
        <div class="col-md-6" v-if="widget.showSubject">
          <label class="form-label" :class="{ 'required': widget.subjectRequired }">
            {{ widget.subjectLabel || 'Θέμα' }}
          </label>
          <input 
            type="text" 
            v-model="formData.subject"
            class="form-control"
            :placeholder="widget.subjectPlaceholder || 'Θέμα μηνύματος'"
            :required="widget.subjectRequired"
          >
        </div>

        <!-- Message Field -->
        <div class="col-12">
          <label class="form-label required">
            {{ widget.messageLabel || 'Μήνυμα' }}
          </label>
          <textarea 
            v-model="formData.message"
            class="form-control"
            :rows="widget.messageRows || 5"
            :placeholder="widget.messagePlaceholder || 'Το μήνυμά σας...'"
            required
          ></textarea>
        </div>

        <!-- GDPR Checkbox -->
        <div class="col-12" v-if="widget.showGdpr">
          <div class="form-check">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="formData.gdprConsent"
              id="gdpr-checkbox"
              required
            >
            <label class="form-check-label" for="gdpr-checkbox">
              {{ widget.gdprText || 'Συμφωνώ με την επεξεργασία των προσωπικών μου δεδομένων' }}
            </label>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="col-12">
          <div class="form-submit" :style="{ textAlign: widget.buttonAlignment || 'left' }">
            <button 
              type="submit" 
              :class="['btn', buttonClass]"
              :disabled="isSubmitting"
              :style="buttonStyles"
            >
              <span v-if="!isSubmitting">
                <i v-if="widget.buttonIcon" :class="widget.buttonIcon + ' me-2'"></i>
                {{ widget.buttonText || 'Αποστολή' }}
              </span>
              <span v-else>
                <i class="spinner-border spinner-border-sm me-2"></i>
                Αποστολή...
              </span>
            </button>
          </div>
        </div>
      </div>
    </form>

    <!-- Success Message -->
    <div v-if="showSuccessMessage" class="alert alert-success mt-3" :style="{ textAlign: 'center' }">
      <i class="bi bi-check-circle me-2"></i>
      {{ widget.successMessage || 'Το μήνυμά σας στάλθηκε επιτυχώς!' }}
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="alert alert-danger mt-3">
      <i class="bi bi-exclamation-circle me-2"></i>
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { authFetch } from '@/utils/authFetch'
import type { ContactFormWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'

interface Props {
  widget: ContactFormWidget
}

const props = defineProps<Props>()

const API_BASE_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/backend/api`

// Form data
const formData = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  gdprConsent: false
})

// Form state
const isSubmitting = ref(false)
const showSuccessMessage = ref(false)
const errorMessage = ref('')

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '2rem',
  ...resolveWidgetBackgroundStyle(props.widget),
  borderRadius: props.widget.borderRadius || '0px',
  border: props.widget.showBorder ? `1px solid ${props.widget.borderColor || '#dee2e6'}` : 'none',
  boxShadow: props.widget.showShadow ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || '#333',
  fontSize: props.widget.titleSize || '1.5rem',
  textAlign: props.widget.titleAlignment || 'left',
  marginBottom: '1rem'
}))

const descriptionStyles = computed(() => ({
  color: props.widget.descriptionColor || '#666',
  fontSize: props.widget.descriptionSize || '1rem',
  textAlign: props.widget.descriptionAlignment || 'left'
}))

const buttonClass = computed(() => {
  const style = props.widget.buttonStyle || 'primary'
  return `btn-${style}`
})

const buttonStyles = computed(() => ({
  backgroundColor: props.widget.customButtonColor || undefined,
  borderColor: props.widget.customButtonColor || undefined,
  fontSize: props.widget.buttonSize === 'small' ? '0.875rem' : 
            props.widget.buttonSize === 'large' ? '1.125rem' : '1rem',
  padding: props.widget.buttonSize === 'small' ? '0.375rem 0.75rem' :
           props.widget.buttonSize === 'large' ? '0.75rem 1.5rem' : '0.5rem 1rem'
}))

// Submit form
const submitForm = async () => {
  if (isSubmitting.value) return

  // Validate name fields
  if (!formData.value.firstName && !formData.value.lastName) {
    errorMessage.value = 'Παρακαλώ συμπληρώστε τουλάχιστον το Όνομα ή το Επώνυμο'
    return
  }

  isSubmitting.value = true
  errorMessage.value = ''
  showSuccessMessage.value = false

  try {
    const payload = {
      action: 'contact_form',
      form_id: props.widget.id,
      form_title: props.widget.title || 'Contact Form',
      ...formData.value
    }

    const response = await authFetch(`${API_BASE_URL}/contact.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })

    const result = await response.json()

    if (result.success) {
      showSuccessMessage.value = true
      // Reset form
      formData.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        gdprConsent: false
      }
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        showSuccessMessage.value = false
      }, 5000)
    } else {
      errorMessage.value = result.message || 'Σφάλμα κατά την αποστολή του μηνύματος'
    }
  } catch (error) {
    errorMessage.value = 'Σφάλμα δικτύου. Παρακαλώ δοκιμάστε ξανά.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.contact-form-widget {
  max-width: 100%;
  margin: 0 auto;
}

.form-title h3 {
  margin: 0;
  font-weight: 600;
}

.form-description p {
  margin: 0;
  line-height: 1.5;
}

.form-label.required::after {
  content: " *";
  color: #dc3545;
}

.form-control {
  border-radius: 0.375rem;
  border: 1px solid #ced4da;
  padding: 0.75rem;
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.alert {
  border-radius: 0.375rem;
  border: none;
}

.alert-success {
  background-color: #d1edff;
  color: #0c5460;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

@media (max-width: 768px) {
  .contact-form-widget {
    padding: 1rem !important;
  }
  
  .col-md-6 {
    margin-bottom: 1rem;
  }
}
</style>